import { db } from '@/services/firebaseService';
import { doc, getDoc, setDoc } from 'firebase/firestore';

export interface SystemPromptRecord {
  lastEditedAt: Date;
  prompt: string;
  programKey: string;
}

export interface SystemPromptUpdate {
  prompt: string;
  programKey: string;
}

/**
 * Service for managing custom system prompts for session date extraction
 */
export class SystemPromptService {
  
  /**
   * Get all system prompts for a location
   */
  static async getPrompts(locationId: string): Promise<SystemPromptRecord[]> {
    try {
      console.log(`[SystemPromptService] Loading prompts for location: ${locationId}`);
      
      const configDocRef = doc(db, 'campLocations', locationId, 'fetch', '_config');
      const configDoc = await getDoc(configDocRef);
      
      if (!configDoc.exists()) {
        console.log(`[SystemPromptService] No prompts config found for location: ${locationId}`);
        return [];
      }
      
      const data = configDoc.data();
      const prompts = data?.prompts_fetchSessionsFromProgram || [];
      
      // Convert Firestore timestamps to Date objects
      const convertedPrompts = prompts.map((prompt: any) => ({
        ...prompt,
        lastEditedAt: prompt.lastEditedAt?.toDate() || new Date()
      }));
      
      console.log(`[SystemPromptService] Loaded ${convertedPrompts.length} prompts for location: ${locationId}`);
      return convertedPrompts;
      
    } catch (error) {
      console.error(`[SystemPromptService] Error loading prompts:`, error);
      throw new Error(`Failed to load system prompts: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  /**
   * Get system prompt for a specific program
   */
  static async getPromptForProgram(locationId: string, programKey: string): Promise<SystemPromptRecord | null> {
    try {
      const prompts = await this.getPrompts(locationId);
      const matchingPrompt = prompts.find(p => p.programKey === programKey);
      
      console.log(`[SystemPromptService] Found prompt for program ${programKey}:`, !!matchingPrompt);
      return matchingPrompt || null;
      
    } catch (error) {
      console.error(`[SystemPromptService] Error getting prompt for program:`, error);
      throw new Error(`Failed to get system prompt for program: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  /**
   * Update or create system prompt for a program
   */
  static async updatePromptForProgram(
    locationId: string, 
    update: SystemPromptUpdate
  ): Promise<void> {
    try {
      console.log(`[SystemPromptService] Updating prompt for program: ${update.programKey} in location: ${locationId}`);
      
      // Get existing prompts
      const existingPrompts = await this.getPrompts(locationId);
      
      // Find existing prompt for this program
      const existingIndex = existingPrompts.findIndex(p => p.programKey === update.programKey);
      
      if (existingIndex >= 0) {
        // Update existing prompt
        existingPrompts[existingIndex] = {
          ...existingPrompts[existingIndex],
          prompt: update.prompt,
          lastEditedAt: new Date()
        };
        console.log(`[SystemPromptService] Updated existing prompt for program: ${update.programKey}`);
      } else {
        // Add new prompt
        existingPrompts.push({
          lastEditedAt: new Date(),
          prompt: update.prompt,
          programKey: update.programKey
        });
        console.log(`[SystemPromptService] Created new prompt for program: ${update.programKey}`);
      }
      
      // Save back to Firestore
      const configDocRef = doc(db, 'campLocations', locationId, 'fetch', '_config');
      await setDoc(configDocRef, {
        prompts_fetchSessionsFromProgram: existingPrompts
      }, { merge: true });
      
      console.log(`[SystemPromptService] Successfully saved prompt for program: ${update.programKey}`);
      
    } catch (error) {
      console.error(`[SystemPromptService] Error updating prompt:`, error);
      throw new Error(`Failed to update system prompt: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  /**
   * Get the default system prompt template
   */
  static getDefaultPromptTemplate(): string {
    return `*** Our goal is to extract the essential 'session' information for a given camp 'program' from the sent sanitized HTML snippet. Find sessions that appear to conform to the age/grade/eligibility requirements of the 'program' JSON below. Extract a list of distinct date ranges (startDate and endDate) for all such sessions, as well as the corresponding 'price', 'displayTitle', 'url', and 'description' (max 500 characters) for each of those date ranges. Return your response in a tabular text format. ***

## PROGRAM JSON:
{{PROGRAM_JSON}}

## EXTRACTION REQUIREMENTS:
1. Find all session date ranges that match this program's age/grade requirements
2. Extract startDate and endDate in YYYY-MM-DD format
3. Extract price information (include currency symbol if present)
4. Extract session title/displayTitle
5. Extract the session URL (where users can learn about the session) if available, or if not, then use the URL of the page where the Program information is found
6. Extract session description (max 500 characters)
7. Return results in tabular text format (not JSON)

## OUTPUT FORMAT:
Return a simple table with the following columns separated by | (pipe characters):
startDate | endDate | price | displayTitle | url | description

Example:
2025-06-09 | 2025-06-13 | $299 | Summer Science Camp Week 1 | https://example.com/register/week1 | Fun science activities for grades K-1 including experiments and exploration
2025-06-16 | 2025-06-20 | $299 | Summer Science Camp Week 2 | https://example.com/register/week2 | Advanced science activities for grades K-1 with hands-on learning

## SANITIZED HTML CONTENT TO ANALYZE:
{{SANITIZED_HTML}}`;
  }
} 